import {
  useState,
  useEffect,
  Dispatch, useCallback,
} from 'react';
import isEqual from 'lodash/isEqual';

import { logger } from '@utils/logger';

const useStorageState = <T>(
  storageType: typeof localStorage | typeof sessionStorage | undefined,
  key: string,
  defaultValue?: T,
): [T | undefined, Dispatch<T>] => {
  const getStorageItemOrDefault = () => {
    let storageItem;
    try {
      storageItem = storageType && storageType.getItem(key);
    } catch (e) {
      logger.warn(e);
    }

    if (storageItem) {
      return JSON.parse(storageItem) as T | undefined;
    }

    return defaultValue;
  };

  // Internal state
  const [state, setState] = useState(
    getStorageItemOrDefault,
  );

  // Update internal hook state if storage updated
  useEffect(
    () => {
      const handler = (event: StorageEvent) => {
        if (event.key === key) {
          const storageItem = getStorageItemOrDefault();
          setState(storageItem);
        }
      };

      window.addEventListener('storage', handler);

      return () => window.removeEventListener('storage', handler);
    },
    // TODO: refactor this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Update only storage, internal state will be updated by event listener
  const setStateAndUpdateStorage = useCallback(
    (value: T) => {
      if (!storageType) {
        throw new Error('Undefined storageType');
      }

      if (value === null || value === undefined) {
        storageType.removeItem(key);
        setState(defaultValue);
      } else if (!isEqual(value, state)) {
        const jsonValue = JSON.stringify(value);
        storageType.setItem(key, jsonValue);
        setState(value);
      }
    },
     
    [defaultValue, setState, state, key, storageType],
  );

  return [state, setStateAndUpdateStorage];
};

export const usePersistentState = <T>(
  key: string,
  defaultValue?: T,
): [T | undefined, Dispatch<T>] => (
    useStorageState<T>(typeof window !== 'undefined' ? localStorage : undefined, key, defaultValue)
  );

export const useSessionState = <T>(
  key: string,
  defaultValue?: T,
): [T | undefined, Dispatch<T>] => (
    useStorageState<T>(typeof window !== 'undefined' ? sessionStorage : undefined, key, defaultValue)
  );
